// Share-image footers. Every server-rendered share image ends with
// "aluvo.app · <tagline>"; the tagline matches what is being shared unless
// the sharer picks another one in the builder's Footer message selector.
//
// Choices are stable keys (stored on the device and sent as `footer=` on the
// card URLs); the text lives only here and in the backend mirror
// (apps/backend/src/utils/shareFooter.js, checked by its test).
//   default   the share type's own tagline (SHARE_FOOTER_DEFAULTS)
//   discover / collect / prove   a fixed tagline (SHARE_FOOTER_MESSAGES)
//   none      no tagline; "aluvo.app" stays on every image
//
// Share types: title (a movie or show), watchlist, ranking (Custom Rankings,
// My Top Movies), collection (My Lists, Starter Picks), coin (a quiz
// invitation or an earned coin).

export const SHARE_FOOTER_ATTRIBUTION = 'aluvo.app';
export const SHARE_FOOTER_TYPES = ['title', 'watchlist', 'ranking', 'collection', 'coin'];
export const SHARE_FOOTER_CHOICES = ['default', 'discover', 'collect', 'prove', 'none'];
export const DEFAULT_SHARE_FOOTER = 'default';

export const SHARE_FOOTER_MESSAGES = {
    discover: 'Discover your next watch.',
    collect: 'Discover. Collect. Share.',
    prove: 'You’ve watched it. Now prove it.',
};

/** Each type's own tagline; a ranking names its real number of saved titles. */
export const SHARE_FOOTER_DEFAULTS = {
    title: () => SHARE_FOOTER_MESSAGES.discover,
    watchlist: () => 'Your next movie night starts here.',
    ranking: ({ count } = {}) => (Number(count) > 0 ? `Make your own Top ${Math.floor(Number(count))}.` : 'Make your own ranking.'),
    collection: () => 'Find your next obsession.',
    coin: () => SHARE_FOOTER_MESSAGES.prove,
};

/** Anything stored or sent → a valid choice (unknown → 'default'). */
export function normalizeShareFooter(value) {
    return SHARE_FOOTER_CHOICES.includes(value) ? value : DEFAULT_SHARE_FOOTER;
}

/** The tagline drawn after "aluvo.app ·" — '' for 'none'. `context.count`: a ranking's saved titles. */
export function shareFooterTagline(type, choice, context = {}) {
    const key = normalizeShareFooter(choice);
    if (key === 'none') return '';
    if (key === 'default') return (SHARE_FOOTER_DEFAULTS[type] ?? SHARE_FOOTER_DEFAULTS.title)(context);
    return SHARE_FOOTER_MESSAGES[key];
}

/** The whole footer line as it reads on the image. */
export function shareFooterText(type, choice, context = {}) {
    const tagline = shareFooterTagline(type, choice, context);
    return tagline ? `${SHARE_FOOTER_ATTRIBUTION} · ${tagline}` : SHARE_FOOTER_ATTRIBUTION;
}

/** The Footer message selector's rows for a share type: { key, label, sub }. */
export function shareFooterOptions(type, context = {}) {
    return [
        { key: 'default', label: 'Default for this share type', sub: shareFooterText(type, 'default', context) },
        { key: 'discover', label: SHARE_FOOTER_MESSAGES.discover, sub: shareFooterText(type, 'discover') },
        { key: 'collect', label: SHARE_FOOTER_MESSAGES.collect, sub: shareFooterText(type, 'collect') },
        { key: 'prove', label: SHARE_FOOTER_MESSAGES.prove, sub: shareFooterText(type, 'prove') },
        { key: 'none', label: 'No message', sub: `Only “${SHARE_FOOTER_ATTRIBUTION}”` },
    ];
}

/** `footer=<choice>` for a card URL. Always explicit, so a cached image never answers for another footer. */
export function shareFooterQuery(choice) {
    return `footer=${normalizeShareFooter(choice)}`;
}
